import { Pipe, PipeTransform } from '@angular/core';
import DOMPurify, { Config } from 'dompurify';
import { ALLOWED_TAGS } from './allowed-tags';

const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [...ALLOWED_TAGS],
    ALLOWED_ATTR: [],
} satisfies Config;

const SANITIZE_CONFIG_WITH_MARK = {
    ...SANITIZE_CONFIG,
    ALLOWED_TAGS: [...SANITIZE_CONFIG.ALLOWED_TAGS, 'mark'],
    ALLOWED_ATTR: ['class'],
} satisfies Config;

@Pipe({ name: 'highlight', standalone: true })
export class HighlightPipe implements PipeTransform {
    transform(html: string, search: string): string {
        if (!search?.trim()) {
            return DOMPurify.sanitize(html, SANITIZE_CONFIG);
        }

        const sanitized = DOMPurify.sanitize(html, SANITIZE_CONFIG);

        const template = document.createElement('template');
        template.innerHTML = sanitized;

        this.highlightTextNodes(template.content, search.toLowerCase());

        const result = template.innerHTML;
        return DOMPurify.sanitize(result, SANITIZE_CONFIG_WITH_MARK);
    }

    private highlightTextNodes(node: Node, searchLower: string): void {
        if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent ?? '';
            const textLower = text.toLowerCase();
            const index = textLower.indexOf(searchLower);

            if (index === -1) return;

            const fragment = document.createDocumentFragment();
            let lastIndex = 0;

            let currentIndex = index;
            while (currentIndex !== -1) {
                // Text before match
                if (currentIndex > lastIndex) {
                    fragment.appendChild(document.createTextNode(text.slice(lastIndex, currentIndex)));
                }

                // Highlighted match
                const mark = document.createElement('mark');
                mark.className = 'bg-primary';
                mark.textContent = text.slice(currentIndex, currentIndex + searchLower.length);
                fragment.appendChild(mark);

                lastIndex = currentIndex + searchLower.length;
                currentIndex = textLower.indexOf(searchLower, lastIndex);
            }

            // Remaining text after last match
            if (lastIndex < text.length) {
                fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
            }

            node.parentNode?.replaceChild(fragment, node);
        } else {
            // Clone childNodes to array since we mutate the DOM during traversal
            Array.from(node.childNodes).forEach((child) => this.highlightTextNodes(child, searchLower));
        }
    }
}
